import "./menu.js";
import "./fv-illusts.js";
import "./title-svg-animate.js";
import { init as initMakimono } from "./makimono.js";
import { $, $$ } from "./utils.js";

// ページカバー：初回のみ表示。?cover=1 のときも表示。
// メイン画像は APNG のため「1周終了」の DOM イベントがない。実画像の load + decode + 2 フレーム待ちのあと、
// data-cover-animation-ms-*（1周の合計 ms）＋ data-cover-animation-safety-ms を待ってからフェードアウトする。
const PAGE_COVER_STORAGE_KEY = "yoshimin_page_cover_seen";

function setRandomLoadingTextImage() {
  const loadingText = $("#loading-text");
  if (!loadingText) return;

  const currentSrc = loadingText.getAttribute("src");
  if (!currentSrc) return;

  const randomIndex = Math.floor(Math.random() * 5) + 1;
  const nextSrc = currentSrc.replace(/loading-text-\d+\.svg$/, `loading-text-${randomIndex}.svg`);
  if (nextSrc !== currentSrc) {
    loadingText.setAttribute("src", nextSrc);
  }
}

function waitDoubleRaf() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

/**
 * `<picture>` 内の実表示 img が読み込まれ、可能なら decode し、次の描画まで待つ。
 * 別インスタンスの Image() プリロードだと「本体 img の再生開始」とズレるため、常にこの要素を基準にする。
 */
function waitCoverMainImagePaintReady(cover) {
  const imgEl = cover.querySelector("picture img");
  if (!(imgEl instanceof HTMLImageElement)) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const finish = () => {
      resolve();
    };

    const afterLoaded = () => {
      if (!imgEl.naturalWidth) {
        finish();
        return;
      }
      const decoded = imgEl.decode ? imgEl.decode().catch(() => {}) : Promise.resolve();
      decoded.then(() => waitDoubleRaf()).then(finish);
    };

    if (imgEl.complete) {
      afterLoaded();
    } else {
      imgEl.addEventListener("load", afterLoaded, { once: true });
      imgEl.addEventListener("error", finish, { once: true });
    }
  });
}

function getCoverAnimationDurationMs(cover) {
  const isMdUp = window.matchMedia("(min-width: 768px)").matches;
  const fallback = cover.getAttribute("data-cover-animation-ms");
  const raw = isMdUp
    ? cover.getAttribute("data-cover-animation-ms-pc") ?? fallback
    : cover.getAttribute("data-cover-animation-ms-sp") ?? fallback;
  const n = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : 3000;
}

function getCoverAnimationSafetyMs(cover) {
  const raw = cover.getAttribute("data-cover-animation-safety-ms");
  const n = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : 120;
}

/** #page-cover が閉じたときに .fv-illusts をアニメーション可能にする（クラス付与 + カスタムイベント発火） */
function setFvIllustsReady() {
  const container = $("#fv-illusts");
  if (container) {
    container.classList.add("is-ready");
    container.setAttribute("aria-hidden", "false");
    container.dispatchEvent(new CustomEvent("fvillusts:ready", { bubbles: true }));
  }
}

function initPageCover() {
  const cover = $("#page-cover");
  if (!cover) return;

  const searchParams = new URLSearchParams(window.location.search);
  const forceCover = searchParams.get("cover") === "1";
  const hasSeenBefore = !!localStorage.getItem(PAGE_COVER_STORAGE_KEY);

  if (!forceCover && hasSeenBefore) {
    cover.classList.add("is-hidden");
    document.body.classList.remove("is-boot");
    setFvIllustsReady();
    return;
  }

  document.body.classList.add("is-first-visit");

  let coverDismissed = false;

  function hideCover() {
    if (coverDismissed) return;
    coverDismissed = true;
    cover.classList.add("is-hidden");
    document.body.classList.remove("is-boot", "is-first-visit");
    localStorage.setItem(PAGE_COVER_STORAGE_KEY, "1");
    setFvIllustsReady();
  }

  let hideTimeoutId = null;

  waitCoverMainImagePaintReady(cover).then(() => {
    if (coverDismissed) return;
    const holdMs = getCoverAnimationDurationMs(cover) + getCoverAnimationSafetyMs(cover);
    hideTimeoutId = window.setTimeout(hideCover, holdMs);
  });

  cover.addEventListener("click", () => {
    if (hideTimeoutId !== null) {
      window.clearTimeout(hideTimeoutId);
      hideTimeoutId = null;
    }
    hideCover();
  });
}

// アンカーリンクのスムーススクロール
window.addEventListener("DOMContentLoaded", () => {
  setRandomLoadingTextImage();

  // SP用ランダム1枚は fv-illust アニメーションより前に決定する
  const items = $$('.js-random');
  if (items.length > 0) {
    items.forEach(item => item.classList.add('max-lg:opacity-0'));
    const randomIndex = Math.floor(Math.random() * items.length);
    items[randomIndex].classList.remove('max-lg:opacity-0');
  }

  initPageCover();

  const popupButton = $(".popup-button");
  if (popupButton) popupButton.classList.add("is-visible");

  $$('a[href^="#"]').forEach((link) => {
    link.addEventListener("click", (e) => {
      const href = link.getAttribute("href");
      if (href === "#") return;
      const target = $(href);
      if (target) {
        e.preventDefault();
        const easing = (t) =>
          t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
        const start = window.pageYOffset;
        const end = target.getBoundingClientRect().top + start;
        const duration = 400;
        const startTime = performance.now();

        function animate(currentTime) {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          window.scrollTo(0, start + (end - start) * easing(progress));
          if (progress < 1) requestAnimationFrame(animate);
        }
        requestAnimationFrame(animate);
      }
    });
  });

  // モーダル
  const modalTriggers = $$('[data-friend]');
  modalTriggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const id = trigger.getAttribute('data-friend');
      const targetModal = $(`[data-modal="${id}"]`);

      if (targetModal) {
        targetModal.classList.remove('opacity-0','invisible');
        document.body.style.overflow = 'hidden';

        targetModal.onclick = () => {
          targetModal.classList.add('opacity-0', 'invisible');
          document.body.style.overflow = 'auto';
        };
      }
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const allModals = $$('[data-modal]');

      allModals.forEach(modal => {
        if (!modal.classList.contains('opacity-0')) {
          modal.classList.add('opacity-0', 'invisible');
          document.body.style.overflow = 'auto';
        }
      });
    }
  });

  // スクロールアニメーション
  const observerCallback = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-inview');
      }
    });
  };
  const defaultObserverOptions = {
    threshold: 0.3,
  };
  const defaultObserver = new IntersectionObserver(observerCallback, defaultObserverOptions);
  const targetElements = document.querySelectorAll('.js-fade, .js-filter');
  targetElements.forEach((target) => {
    defaultObserver.observe(target);
  });

  initMakimono();
});



